import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { CheckCircle, Fingerprint, EnvelopeSimple } from "@phosphor-icons/react";

export default function CardholderLogin() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { login, completeMfa, applySession } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("password"); // password | magic | mfa
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  const safeNext = (() => {
    const raw = params.get("next") || "";
    return raw.startsWith("/") && !raw.startsWith("//") ? raw : "";
  })();

  const afterLogin = (user) => {
    if (safeNext) return nav(safeNext);
    if (user?.role === "cardholder") nav("/my-cards");
    else nav("/");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(form.email.trim().toLowerCase(), form.password);
    setSubmitting(false);
    if (!res.ok) return setError(res.error);
    if (res.mfa_required) {
      setMfaToken(res.mfa_token);
      setMode("mfa");
      return;
    }
    afterLogin(res.user);
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await completeMfa(mfaToken, mfaCode);
    setSubmitting(false);
    if (!res.ok) return setError(res.error);
    afterLogin(res.user);
  };

  const requestMagic = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/magic-link/request", { email: form.email.trim().toLowerCase() });
      setMagicSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const passkeyLogin = async () => {
    setError("");
    setSubmitting(true);
    try {
      const email = form.email.trim().toLowerCase() || undefined;
      const { data } = await api.post("/auth/webauthn/login/options", email ? { email } : {});
      const assertion = await startAuthentication({ optionsJSON: data.options });
      const done = await api.post("/auth/webauthn/login/complete", { credential: assertion });
      const res = applySession(done.data);
      if (!res.ok) return setError(res.error || "Passkey login failed");
      afterLogin(res.user);
    } catch (err) {
      setError(formatApiError(err) || "Passkey login failed or cancelled");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]">
        <div className="max-w-md mx-auto px-4 py-12 sm:py-16">
          <div className="label-tag text-neutral-500">Cardholder</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Log in to manage your card</h1>

          {mode === "mfa" ? (
            <form onSubmit={submitMfa} className="mt-8 space-y-4 bg-white border border-black p-6" data-testid="cardholder-mfa-form">
              <p className="text-sm text-neutral-700">Enter the 6-digit code from your authenticator app (or a backup code).</p>
              <div>
                <label className="label-tag">Authentication code</label>
                <input
                  className="mt-1 w-full px-4 py-3 border border-black outline-none font-mono-cc tracking-widest"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  data-testid="ch-mfa-code"
                  autoFocus
                  required
                />
              </div>
              {error && <div className="label-tag text-red-600" data-testid="ch-login-error">{error}</div>}
              <button type="submit" disabled={submitting} className="w-full bg-black text-white label-tag px-4 py-4 brutal-btn disabled:opacity-60" data-testid="ch-mfa-submit">
                {submitting ? "Verifying…" : "Verify & sign in"}
              </button>
              <button type="button" className="w-full label-tag underline text-sm" onClick={() => { setMode("password"); setError(""); }}>
                Back to password
              </button>
            </form>
          ) : mode === "magic" ? (
            <form onSubmit={requestMagic} className="mt-8 space-y-4 bg-white border border-black p-6" data-testid="cardholder-magic-form">
              {magicSent ? (
                <div data-testid="magic-link-sent">
                  <p className="text-sm text-neutral-800">If that email has an account, we sent a sign-in link. Check your inbox.</p>
                  <button type="button" className="mt-4 label-tag underline text-sm" onClick={() => { setMode("password"); setMagicSent(false); }}>
                    Back to password
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-neutral-700">We'll email you a one-time link. No password needed.</p>
                  <div>
                    <label className="label-tag">Email</label>
                    <input type="email" className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ch-magic-email" required />
                  </div>
                  {error && <div className="label-tag text-red-600">{error}</div>}
                  <button type="submit" disabled={submitting} className="w-full bg-black text-white label-tag px-4 py-4 brutal-btn flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ch-magic-submit">
                    <EnvelopeSimple size={16} weight="bold" /> {submitting ? "Sending…" : "Email me a login link"}
                  </button>
                  <button type="button" className="w-full label-tag underline text-sm" onClick={() => setMode("password")}>
                    Back to password
                  </button>
                </>
              )}
            </form>
          ) : (
            <form onSubmit={submitPassword} className="mt-8 space-y-4 bg-white border border-black p-6" data-testid="cardholder-login-form">
              <div>
                <label className="label-tag">Email</label>
                <input type="email" className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ch-login-email" required />
              </div>
              <div>
                <label className="label-tag">Password</label>
                <input type="password" className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="ch-login-password" required />
              </div>
              {error && <div className="label-tag text-red-600" data-testid="ch-login-error">{error}</div>}
              <button type="submit" disabled={submitting} className="w-full bg-black text-white label-tag px-4 py-4 brutal-btn flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ch-login-submit">
                <CheckCircle size={16} weight="bold" /> {submitting ? "Signing in…" : "Sign in"}
              </button>
              <div className="grid gap-2">
                <button type="button" disabled={submitting} onClick={() => setMode("magic")} className="w-full border border-black label-tag px-4 py-3 brutal-btn flex items-center justify-center gap-2" data-testid="ch-login-magic">
                  <EnvelopeSimple size={16} weight="bold" /> Email me a login link
                </button>
                <button type="button" disabled={submitting} onClick={passkeyLogin} className="w-full border border-black label-tag px-4 py-3 brutal-btn flex items-center justify-center gap-2" data-testid="ch-login-passkey">
                  <Fingerprint size={16} weight="bold" /> Sign in with passkey
                </button>
              </div>
              <div className="flex justify-between text-sm flex-wrap gap-2">
                <Link to="/forgot-password" className="label-tag underline" data-testid="forgot-password-link">Forgot password?</Link>
                <Link to="/recover-email" className="label-tag underline" data-testid="recover-email-link">Forgot email?</Link>
                <Link to="/cardholder/register" className="label-tag underline" data-testid="ch-login-register-link">Sign up</Link>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
